// src/app/features/conversations/FloatingChat/FloatingChatPanel.tsx
import React, { useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { MemoryRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from 'app/redux/store';
import FloatingMessagesRoutes from './FloatingMessagesRoutes';
import { useAuth, UserContext } from 'utils/AuthProvider';
import { useFloatingChat } from './FloatingChatProvider';
import PopupManager from 'Components/Popups/PopupManager';
import ToolTipProvider from 'Components/Popups/Tooltip/Provider';

export interface FloatingChatPanelProps {
  isOpen: boolean;
}

/**
 * The actual chat panel rendered in a portal.
 * If `isOpen` is false, we render nothing.
 */
export default function FloatingChatPanel({ isOpen }: FloatingChatPanelProps) {
  const rootRef = useRef<Root | null>(null);
  const auth = useAuth();
  const { closeChat } = useFloatingChat();

  useEffect(() => {
    const container = document.getElementById('floating-chat-root');
    if (!container) return;

    if (isOpen) {
      // create a separate React root so the MemoryRouter is not nested
      const root = createRoot(container);
      root.render(
        <Provider store={store}>
          <UserContext.Provider value={auth}>
            <PopupManager />
            <ToolTipProvider>
              <div
                style={{
                  position: 'fixed',
                  bottom: '5rem',
                  right: '1rem',
                  width: '350px',
                  maxWidth: '90vw',
                  height: '600px',
                  maxHeight: '90vh',
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '0.5rem',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                  zIndex: 40,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    padding: '0.5rem',
                    borderBottom: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 600,
                  }}
                >
                  <button
                    onClick={closeChat}
                    style={{
                      marginRight: '0.5rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                  <span style={{ flex: 1, textAlign: 'center' }}>Messages</span>
                </div>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <MemoryRouter>
                    <FloatingMessagesRoutes />
                  </MemoryRouter>
                </div>
              </div>
            </ToolTipProvider>
          </UserContext.Provider>
        </Provider>,
      );
      rootRef.current = root;
    } else {
      rootRef.current?.unmount();
      rootRef.current = null;
    }

    return () => {
      rootRef.current?.unmount();
      rootRef.current = null;
    };
  }, [isOpen, auth]);

  return null;
}
